import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Calendar,
  Clock,
  MapPin,
  DollarSign,
  Users,
  Eye,
  Video,
  ArrowLeft
} from 'lucide-react';

import { getProject } from '../lib/api/requirementsApi';
import PageTitle from '../components/generic/PageTitle';
import Badge from '../components/generic/Badge';
import Breadcrumbs from '../components/generic/Breadcrumbs';
import Loader from '../components/generic/Loader';

const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectData, setProjectData] = useState(null);

  useEffect(() => {
    const fetchProjectDetails = async () => {
      try {
        setLoading(true);
        const response = await getProject(id);

        if (response && response.data) {
          setProjectData(response.data);
        } else {
          setError('Failed to load project details');
        }
      } catch (err) {
        console.error('Error fetching project:', err);
        setError(err.message || 'Failed to load project details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProjectDetails();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <h3 className="font-semibold mb-1">Error Loading Project</h3>
          <p className="text-sm">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-3 text-sm text-red-600 hover:text-red-800 font-medium"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!projectData || !projectData.project) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
          <p>Project not found</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-3 text-sm text-yellow-600 hover:text-yellow-800 font-medium"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const { project, assignedCrew = [], assignedEquipment = [] } = projectData;

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get status badge colors
  const getStatusBadge = (status) => {
    const statusMap = {
      active: { text: 'Active', textColor: 'text-green-600', bgColor: 'bg-green-50' },
      confirmed: { text: 'Confirmed', textColor: 'text-indigo-600', bgColor: 'bg-indigo-50' },
      completed: { text: 'Completed', textColor: 'text-gray-600', bgColor: 'bg-gray-50' },
      cancelled: { text: 'Cancelled', textColor: 'text-red-600', bgColor: 'bg-red-50' }
    };

    return statusMap[status] || statusMap.active;
  };

  const statusBadge = getStatusBadge(project.status || 'active');

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Breadcrumbs */}
      <Breadcrumbs overrides={{ [id]: project.project_name || 'Project Details' }} />

      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div className="flex-1">
          <PageTitle
            title={project.project_name || 'Untitled Project'}
            description="View and manage project details"
          />
        </div>
        <Badge
          text={statusBadge.text}
          textColor={statusBadge.textColor}
          bgColor={statusBadge.bgColor}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Event Details Card */}
        <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Video className="w-5 h-5 text-indigo-600" />
            Event Details
          </h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Event Date</p>
                <p className="text-foreground">{formatDate(project.event_date)}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Duration</p>
                <p className="text-foreground">{project.duration_hours || 0} hours</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Location</p>
                <p className="text-foreground">
                  {project.event_location || project.location_data?.placeName || 'Location TBD'}
                </p>
              </div>
            </div>

            {project.event_type && (
              <div className="flex items-start gap-3">
                <Video className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Event Type</p>
                  <p className="text-foreground capitalize">{project.event_type}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Budget & Scale Card */}
        <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Budget & Scale
          </h3>
          <div className="space-y-4">
            {project.budget && (
              <div className="flex items-start gap-3">
                <DollarSign className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Budget</p>
                  <p className="text-foreground font-semibold">${parseFloat(project.budget).toLocaleString()}</p>
                </div>
              </div>
            )}

            {project.expected_viewers && (
              <div className="flex items-start gap-3">
                <Eye className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Expected Viewers</p>
                  <p className="text-foreground">{parseInt(project.expected_viewers).toLocaleString()}</p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Crew Size Needed</p>
                <p className="text-foreground">{project.crew_size_needed || 0} members</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Description Section */}
      {project.description && (
        <div className="bg-card rounded-xl border border-border p-6 shadow-sm mb-8">
          <h3 className="text-lg font-semibold text-foreground mb-3">Description</h3>
          <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
            {project.description}
          </p>
        </div>
      )}

      {/* Assigned Crew Section */}
      {assignedCrew.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-6 shadow-sm mb-8">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            Assigned Crew ({assignedCrew.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assignedCrew.map((crew, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-3 bg-muted rounded-lg border border-border"
              >
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">
                    {crew.crew?.name || crew.name || 'Crew Member'}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">
                    {crew.crew?.role || crew.role || 'Role not specified'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Assigned Equipment Section */}
      {assignedEquipment.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-6 shadow-sm mb-8">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Video className="w-5 h-5 text-blue-600" />
            Assigned Equipment ({assignedEquipment.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assignedEquipment.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-3 bg-muted rounded-lg border border-border"
              >
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Video className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">
                    {item.equipment?.equipment_name || item.name || 'Equipment'}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">
                    {item.equipment?.category || 'Category not specified'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 justify-end">
        <button
          onClick={() => navigate('/dashboard')}
          className="px-4 py-2 rounded-lg border border-border bg-card text-foreground hover:bg-muted transition-colors flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
      </div>
    </div>
  );
};

export default ProjectDetailPage;
